import React, { useState } from "react";
import PropTypes from "prop-types";
import { formatTimestamp, responderStatusProps } from "./pdActivityUtils";
import styles from "./PagerDutyActivity.module.css";

const RESPONDER_PAGE_SIZE = 10;

/**
 * PagerDutyResponderList — paginated flat list of incident-level responders
 * with status badges. Used for the Individuals sub-block inside
 * PagerDutyEscalationGroupList.
 *
 * Props:
 *   responders   {Array}   responder objects
 *   listLabel    {string}  accessible name for the role="list" element
 *   emptyMessage {string}  text shown when the list is empty
 */
function PagerDutyResponderList({
  responders,
  listLabel = "Responders",
  emptyMessage = "No responders recorded for this incident.",
}) {
  const [showAll, setShowAll] = useState(false);

  if (responders.length === 0) {
    return (
      <p className={styles.emptySection}>
        {emptyMessage}
      </p>
    );
  }

  const visible = showAll ? responders : responders.slice(0, RESPONDER_PAGE_SIZE);
  const hiddenCount = responders.length - RESPONDER_PAGE_SIZE;

  return (
    <div className={styles.responderList} role="list" aria-label={listLabel}>
      {visible.map((r, idx) => {
        const { cls, label } = responderStatusProps(r.status);
        return (
          <div key={r.id || r.name || idx} className={styles.responderRow} role="listitem">
            <span className={styles.responderName}>{r.name || "Unknown"}</span>
            <div className={styles.responderRight}>
              {r.at && (
                <span className={styles.responderTime}>
                  {formatTimestamp(r.at)}
                </span>
              )}
              <span
                className={`${styles.statusBadge} ${cls}`}
                aria-label={`Status: ${label}`}
              >
                {label}
              </span>
            </div>
          </div>
        );
      })}

      {!showAll && hiddenCount > 0 && (
        <button
          className={styles.showMoreBtn}
          onClick={() => setShowAll(true)}
          aria-label={`Show ${hiddenCount} more responder${hiddenCount !== 1 ? "s" : ""}`}
        >
          Show {hiddenCount} more responder{hiddenCount !== 1 ? "s" : ""}
        </button>
      )}
      {showAll && responders.length > RESPONDER_PAGE_SIZE && (
        <button
          className={styles.showMoreBtn}
          onClick={() => setShowAll(false)}
          aria-label="Show fewer responders"
        >
          Show fewer responders
        </button>
      )}
    </div>
  );
}

PagerDutyResponderList.propTypes = {
  responders: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      status: PropTypes.string,
      at: PropTypes.string,
    })
  ).isRequired,
  listLabel: PropTypes.string,
  emptyMessage: PropTypes.string,
};

export default PagerDutyResponderList;
